import HomePpConnectPrompt from "./HomePpConnectPrompt";
import MemberSearchWidget from "./MemberSearchWidget";
import HospitalEdEventsWidget from "./HospitalEdEventsWidget";
import HomeUpNextWidget from "./UpNextWidget";
import UrgentPatientsWidget from "./UrgentPatientsWidget";

/**
 * Top-level Home landing page — the app's default route (`/`  ->  `/home`).
 * Owns the heading and the slot its widgets mount into.
 *
 * Renders bare (no page header/nav of its own) — in shell mode the chrome
 * comes from TabbedAppShell's <AppHeader>, and in classic mode from
 * AppLayout, exactly like WorklistPage/PracticeSchedulePage.
 */
export default function HomePage() {
  return (
    <div className="h-full min-h-0 overflow-y-auto p-6">
      {/*
       * Widget slot — the single mount point Home's widgets are appended to,
       * rather than scattering ad-hoc containers around the page.
       *
       * TWO ROWS, not one grid. Search is a FULL-WIDTH row of its own and the
       * PP-backed panel cards sit in columns beneath it. Search earns the
       * whole width because it is the only widget here that GROWS — its
       * result rows are `MemberResultsTable`'s own wide row markup (identity
       * line + plan + eligibility), which a half-width card truncates — and
       * because it is Home's floor: the one widget that renders for every
       * user on every visit, so it should not be the thing squeezed into a
       * column beside cards that may render nothing at all.
       *
       * Further widgets go in whichever row fits: a wide, growing surface
       * joins the top; a fixed-height summary card joins the grid below.
       */}
      <div data-testid="home-widget-slot" className="flex flex-col gap-4">
        {/* Home's floor: the ONLY widget here that renders for every user on
            every visit. Everything below it is gated on a Provider Portal
            session or a priority panel, which is what left Home a bare
            heading before a clinician linked their PP account. Search works
            from day one, so it goes first and Home always has something to
            do. It grows as results land rather than paging away.

            Its own form re-flows to a single 4-across row at `lg:` — the
            SAME breakpoint the grid below splits into columns, so the card
            widens and its fields spread in one step rather than leaving a
            stretched 2x2 block. See MemberSearchCard.tsx's field grid. */}
        <MemberSearchWidget />
        {/* Full-width row, same as Search above and for the same reason: it
            renders a clinical summary (rich markdown + labs), which a
            half-width card would crowd. Renders NOTHING unless the provider
            has both a Practice Schedule grant and an auto-resolved NPI — see
            HomeUpNextWidget's own header. */}
        <HomeUpNextWidget />
        {/*
         * `lg:grid-cols-2` so each card sits at a card's width instead of
         * stretching the full page; `items-start` keeps each at its own
         * natural height rather than stretching the row.
         *
         * TWO columns, not three. The breakpoint is deliberately one step
         * later than the obvious `sm:` (2026-09-10). These are
         * PatientPriorityIndex cards (UrgentPatientsCard /
         * HospitalEdEventsCard) whose rows are truncate-on-narrow by
         * construction: `name (truncate)` + a nowrap `DOB · age · sex`, over
         * a 2- or 3-up metrics strip, plus Hospital/ED's `Discharged to Home
         * · Sep 7 · Facility` detail line. At `sm:` (640px) a two-up split
         * leaves each card ~300px, where the metrics strip crowds and the
         * name and event line are both clipped to a few characters. A
         * stacked full-width card reads better than two clipped ones, so a
         * column is only added once each has ~490px — which `lg:` two-up
         * gives.
         *
         * The `2xl:grid-cols-3` that used to be here went with Search moving
         * out: only two cards live in this row now, so a third column could
         * never be anything but empty.
         */}
        <div className="grid gap-4 items-start lg:grid-cols-2">
          {/* Renders NOTHING once a PP session exists — see its header. It
              shares this row because it stands IN FOR the two cards below:
              without a PP bearer neither of them renders, so the prompt is
              what occupies the columns. */}
          <HomePpConnectPrompt />
          {/* Renders NOTHING unless the provider actually has a priority
              panel — see UrgentPatientsWidget's header. */}
          <UrgentPatientsWidget />
          {/* Renders NOTHING unless the provider actually has a priority
              panel — see HospitalEdEventsWidget's header. */}
          <HospitalEdEventsWidget />
        </div>
      </div>
    </div>
  );
}
